/**
 * Seat Ledger API client — the append-only subscription seat-change ledger,
 * point-in-time snapshots, and the computed seat-history series. Mirrors the
 * pure-fetch service pattern (relative /api/* URLs proxied to sm4rt-api; types
 * from @techgitter/contracts).
 */

import type {
  SubscriptionSeatChange,
  SeatChangeListResponse,
  SubscriptionSeatSnapshot,
  SeatSnapshotListResponse,
  SeatHistoryPoint,
  SeatHistoryResponse,
  CreateSeatChangeRequest,
} from '@techgitter/contracts';

const BASE = '/api';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      /* ignore */
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export interface SeatChangeQuery {
  customerId?: string;
  subscriptionId?: string;
  vendorProductId?: string;
  startDate?: string;
  endDate?: string;
}

export async function fetchSeatChanges(
  params: SeatChangeQuery = {},
  signal?: AbortSignal,
): Promise<SubscriptionSeatChange[]> {
  const qs = new URLSearchParams();
  if (params.customerId) qs.set('customerId', params.customerId);
  if (params.subscriptionId) qs.set('subscriptionId', params.subscriptionId);
  if (params.vendorProductId) qs.set('vendorProductId', params.vendorProductId);
  if (params.startDate) qs.set('startDate', params.startDate);
  if (params.endDate) qs.set('endDate', params.endDate);
  const query = qs.toString();
  const response = await fetch(`${BASE}/seat-changes${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<SeatChangeListResponse>(response, 'Load seat changes');
  return body.items ?? [];
}

export async function fetchSeatSnapshots(
  subscriptionId?: string,
  signal?: AbortSignal,
): Promise<SubscriptionSeatSnapshot[]> {
  const qs = new URLSearchParams();
  if (subscriptionId) qs.set('subscriptionId', subscriptionId);
  const query = qs.toString();
  const response = await fetch(`${BASE}/seat-snapshots${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<SeatSnapshotListResponse>(response, 'Load seat snapshots');
  return body.items ?? [];
}

export async function fetchSeatHistory(
  subscriptionId: string,
  params: { startDate?: string; endDate?: string } = {},
  signal?: AbortSignal,
): Promise<SeatHistoryPoint[]> {
  const qs = new URLSearchParams({ subscriptionId });
  if (params.startDate) qs.set('startDate', params.startDate);
  if (params.endDate) qs.set('endDate', params.endDate);
  const response = await fetch(`${BASE}/reporting/seat-history?${qs.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const body = await readJson<SeatHistoryResponse>(response, 'Load seat history');
  return body.points ?? [];
}

/** Record a manual/administrative seat change (admin only). */
export async function createSeatChange(
  payload: CreateSeatChangeRequest,
  signal?: AbortSignal,
): Promise<SubscriptionSeatChange> {
  const response = await fetch(`${BASE}/seat-changes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return readJson<SubscriptionSeatChange>(response, 'Record seat change');
}
